/**
 * ModelManager - Single pattern with hybrid model selection
 * 
 * Features:
 * - Automatic selection between INT8 (1.6GB) and INT4 (850MB) models
 * - Storage quota detection
 * - Model caching with Cache API
 * - Shared processor across all components
 * - Survives component unmount/remount
 * - No duplicate downloads
 * 
 * Benefits:
 * - Desktop users get INT8 (higher quality)
 * - Mobile users get INT4 (smaller size)
 * - First visit: Downloads and caches (~2-4 minutes)
 * - Subsequent visits: Loads from cache (~5-15 seconds)
 */

import {
  MobileQwen3WebLLMProcessor,
  LOW_LOCAL_QWEN35_2B_RUNTIME_MODEL,
  TINY_LOCAL_QWEN35_08B_RUNTIME_MODEL,
} from './webllm_integration_azure';
import {
  selectBestModel,
  isModelCached,
  clearModelCache,
  clearOldCaches,
  requestPersistentStorage,
  isStoragePersistent,
  ModelConfig,
  ModelSelectionResult,
  getModelUrls
} from './hybrid_model_selection';

export interface ModelLoadProgress {
  loaded: number;
  total: number;
  percentage: number;
  stage: 'checking' | 'downloading' | 'caching' | 'initializing' | 'ready';
  message: string;
}

class ModelManager {
  private static instance: ModelManager;
  private processor: MobileQwen3WebLLMProcessor | null = null;
  private initPromise: Promise<MobileQwen3WebLLMProcessor> | null = null;
  private selectedModel: ModelConfig | null = null;
  private progressCallbacks: Set<(progress: ModelLoadProgress) => void> = new Set();

  private activeRuntime: 'mobile_qwen3' | 'tiny_qwen3' = 'mobile_qwen3';

  // Private constructor (singleton pattern)
  private constructor() {}
  
  /**
   * Get the singleton instance
   */
  public static getInstance(): ModelManager {
    if (!ModelManager.instance) {
      ModelManager.instance = new ModelManager();
    }
    return ModelManager.instance;
  }

  /**
   * Get the WebLLM processor (initializes if needed)
   * Multiple calls return the same instance
   */
  public async getProcessor(): Promise<MobileQwen3WebLLMProcessor> {
    if (this.processor) {
      console.log(this.activeRuntime === 'tiny_qwen3'
        ? 'Reusing existing tiny-local Qwen3.5-0.8B processor'
        : 'Reusing existing low-local Qwen3.5-2B processor');
      return this.processor;
    }

    if (this.initPromise) {
      console.log('Waiting for low-local processor initialization...');
      return this.initPromise;
    }

    console.log('Creating low-local Qwen3.5-2B processor...');
    this.initPromise = this.initializeProcessor();
    this.processor = await this.initPromise;
    return this.processor;
  }
  
  /**
   * Get which runtime is active for this session
   */
  public getActiveRuntime(): 'mobile_qwen3' | 'tiny_qwen3' {
    return this.activeRuntime;
  }

  /**
   * Initialize the processor with automatic model selection
   */
  private async initializeProcessor(): Promise<MobileQwen3WebLLMProcessor> {
    const overallStart = performance.now();
    
    try {
      // ========================================
      // Step 1: Request persistent storage
      // ========================================
      this.reportProgress({
        loaded: 0,
        total: 100,
        percentage: 0,
        stage: 'checking',
        message: 'Checking storage availability...'
      });
      
      const isPersisted = await isStoragePersistent();
      if (!isPersisted) {
        await requestPersistentStorage();
      }
      
      // Clean up old cache versions
      await clearOldCaches();
      
      // ========================================
      // Step 2: Select best model for this device
      // ========================================
      console.log('🔍 Analyzing device storage...');
      const selection: ModelSelectionResult = await selectBestModel();
      this.selectedModel = selection.config;
      
      console.log('📱 Model Selection:');
      console.log(`   Model: ${selection.config.name}`);
      console.log(`   Size: ${(selection.config.size / 1024 / 1024).toFixed(0)} MB`);
      console.log(`   Quantization: ${selection.config.quantization}`);
      console.log(`   Reason: ${selection.reason}`);
      console.log(`   Preferred: ${selection.isPreferred ? 'Yes' : 'No (fallback)'}`);
      
      // Check if model is already cached
      const isCached = await isModelCached(selection.config);
      if (isCached) {
        console.log('✅ Model is cached - fast load incoming!');
        this.reportProgress({
          loaded: 10,
          total: 100,
          percentage: 10,
          stage: 'checking',
          message: 'Model found in cache, loading...'
        });
      } else {
        console.log('⏳ First time loading - this will take a few minutes');
        console.log('💡 Subsequent loads will be much faster!');
        this.reportProgress({
          loaded: 10,
          total: 100,
          percentage: 10,
          stage: 'downloading',
          message: `Downloading ${selection.config.name} (first time only)...`
        });
      }
      
      // ========================================
      // Step 3: Create processor instance
      // ========================================
      const runtimeModel = selection.config.modelId === 'qwen3.5-0.8b-q4f16-webllm'
        ? TINY_LOCAL_QWEN35_08B_RUNTIME_MODEL
        : LOW_LOCAL_QWEN35_2B_RUNTIME_MODEL;

      this.activeRuntime = selection.config.modelId === 'qwen3.5-0.8b-q4f16-webllm'
        ? 'tiny_qwen3'
        : 'mobile_qwen3';

      const processor = new MobileQwen3WebLLMProcessor(runtimeModel);
      
      // ========================================
      // Step 4: Get signed URLs from backend
      // ========================================

      console.log('🔑 Requesting signed URLs from backend...');
      const urlsResponse = await getModelUrls(selection.config.modelId);

      console.log('✅ Received signed URLs');
      console.log(`   Model URL: ${urlsResponse.modelUrl.substring(0, 50)}...`);
      console.log(`   WASM URL: ${urlsResponse.wasmUrl.substring(0, 50)}...`);
      console.log(`   Expires in: ${urlsResponse.expiresIn}s`);
      console.log(`   Remaining downloads: ${urlsResponse.remainingDownloads}`);

      // ========================================
      // Step 5: Initialize processor with signed URLs
      // ========================================
      const success = await processor.initializeWithSignedUrls(
        urlsResponse,
        (loaded: any, total: any, percentage: number) => {
          this.reportProgress({
            loaded,
            total,
            percentage,
            stage: isCached ? 'initializing' : 'downloading',
            message: isCached 
              ? 'Initializing model...'
              : `Downloading: ${percentage.toFixed(1)}%`
          });
        }
      );
      
      if (!success) {
        throw new Error('Failed to initialize WebLLM processor');
      }
      
      // ========================================
      // Step 6: Done!
      // ========================================
      const totalTime = ((performance.now() - overallStart) / 1000).toFixed(1);
      console.log(`✅ Model fully loaded and ready in ${totalTime}s`);
      
      this.reportProgress({
        loaded: 100,
        total: 100,
        percentage: 100,
        stage: 'ready',
        message: 'Model ready!'
      });
      
      return processor;
      
    } catch (error) {
      console.error('❌ Failed to initialize processor:', error);
      
      this.reportProgress({
        loaded: 0,
        total: 100,
        percentage: 0,
        stage: 'checking',
        message: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
      
      throw error;
    }
  }
  
  /**
   * Register a progress callback
   */
  public onProgress(callback: (progress: ModelLoadProgress) => void): () => void {
    this.progressCallbacks.add(callback);
    
    // Return unsubscribe function
    return () => {
      this.progressCallbacks.delete(callback);
    };
  }
  
  /**
   * Report progress to all registered callbacks
   */
  private reportProgress(progress: ModelLoadProgress): void {
    this.progressCallbacks.forEach(callback => {
      try {
        callback(progress);
      } catch (error) {
        console.error('Error in progress callback:', error);
      }
    });
  }
  
  /**
   * Check if processor is ready
   */
  public isReady(): boolean {
    return this.processor !== null;
  }
  
  /**
   * Get information about the selected model
   */
  public getSelectedModel(): ModelConfig | null {
    return this.selectedModel;
  }
  
  /**
   * Reset the processor (useful for debugging)
   */
  public reset(): void {
    if (this.processor) {
      this.processor.dispose();
    }
    this.processor = null;
    this.initPromise = null;
    this.selectedModel = null;
    this.activeRuntime = 'mobile_qwen3';
    console.log('Low/tiny-local processor reset');
  }
  
  /**
   * Clear model cache (forces re-download on next load)
   */
  public async clearCache(): Promise<void> {
    await clearModelCache();
    console.log('🗑️  Cache cleared - next load will re-download');
  }
  
  /**
   * Get cache statistics
   */
  public async getCacheStats(): Promise<{
    isCached: boolean;
    modelName: string | null;
    modelSize: number | null;
  }> {
    if (!this.selectedModel) {
      // Try to determine which model would be selected
      try {
        const selection = await selectBestModel();
        const isCached = await isModelCached(selection.config);
        return {
          isCached,
          modelName: selection.config.name,
          modelSize: selection.config.size
        };
      } catch (error) {
        return {
          isCached: false,
          modelName: null,
          modelSize: null
        };
      }
    }
    
    const isCached = await isModelCached(this.selectedModel);
    return {
      isCached,
      modelName: this.selectedModel.name,
      modelSize: this.selectedModel.size
    };
  }
}

export default ModelManager;